(() => {
  'use strict';

  /* -------------------------------------------------------
     Secondary image hydration (PC1B)
  ------------------------------------------------------- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hydrateSecondaryImage = (card) => {
    const media = card?.querySelector('.cbh-card-media--has-secondary');
    if (!media || media.dataset.cbhSecondaryHydrated === '1') return;

    const template = media.querySelector('template[data-cbh-secondary-image]');
    if (!template) return;

    const fragment = template.content.cloneNode(true);
    const image = fragment.querySelector('.cbh-card__secondary-image');
    if (!image) return;

    media.dataset.cbhSecondaryHydrated = '1';
    media.appendChild(fragment);
    template.remove();

    const markReady = () => media.classList.add('cbh-card-media--secondary-ready');
    if (image.complete && image.naturalWidth > 0) {
      markReady();
    } else {
      image.addEventListener('load', markReady, { once: true });
    }
  };

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!finePointer.matches) return;
      const card = event.target.closest('.product-card-wrapper');
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      hydrateSecondaryImage(card);
    },
    { passive: true }
  );

  /* -------------------------------------------------------
     PC1D — rating hydration after asynchronous card renders
     -------------------------------------------------------
     Lipscore's small product-rating widgets can be inserted after initial page
     load by collection/search filtering and Shopify recommendations. Re-run
     the widget initializer only when a card surface has actually been replaced.
  ------------------------------------------------------- */
  let ratingHydrationTimer = 0;
  let ratingHydrationRetries = 0;

  const hasCardRatingWidgets = () =>
    !!document.querySelector(
      '.product-card-wrapper .lipscore-rating-small, .product-card-wrapper .lipscore-review-count'
    );

  const hydrateCardRatings = () => {
    ratingHydrationTimer = 0;
    if (!hasCardRatingWidgets()) {
      ratingHydrationRetries = 0;
      return;
    }

    try {
      if (window.lipscore && typeof window.lipscore.initWidgets === 'function') {
        window.lipscore.initWidgets();
        ratingHydrationRetries = 0;
        return;
      }

      if (window.lipscore && typeof window.lipscore.reInitWidgets === 'function') {
        window.lipscore.reInitWidgets(true);
        ratingHydrationRetries = 0;
        return;
      }

      if (typeof window.lipscoreInit === 'function') {
        window.lipscoreInit();
        ratingHydrationRetries = 0;
        return;
      }
    } catch (_) {
      /* A later retry is safer than allowing a third-party widget failure to
         interrupt collection/search or recommendation behaviour. */
    }

    if (ratingHydrationRetries < 3) {
      ratingHydrationRetries += 1;
      ratingHydrationTimer = window.setTimeout(hydrateCardRatings, 250 * ratingHydrationRetries);
    } else {
      ratingHydrationRetries = 0;
    }
  };

  const scheduleRatingHydration = (delay = 40) => {
    window.clearTimeout(ratingHydrationTimer);
    ratingHydrationTimer = window.setTimeout(hydrateCardRatings, delay);
  };

  /* -------------------------------------------------------
     PC1B.5R2 — measured equal-card-height synchronisation
     -------------------------------------------------------
     CSS no longer guesses a fixed content height. Each grid is measured at
     natural height, then all visible collection/search cards receive the
     tallest measured content-body height. This keeps the grid perfectly even
     without clipping legitimate content.
  ------------------------------------------------------- */
  const observedGrids = new WeakSet();
  const resizeObservedGrids = new WeakSet();
  const mutationObservers = new WeakMap();
  const gridResizeObserver = window.ResizeObserver
    ? new ResizeObserver((entries) => {
        entries.forEach((entry) => scheduleSync(entry.target));
      })
    : null;
  let syncFrame = 0;

  const getProductGrids = (root = document) => {
    const grids = [];
    if (root instanceof Element && root.matches('#product-grid.product-grid, .product-grid')) {
      grids.push(root);
    }
    root.querySelectorAll?.('#product-grid.product-grid, .product-grid').forEach((grid) => grids.push(grid));
    return [...new Set(grids)];
  };

  const getSyncBodies = (grid) =>
    [...grid.querySelectorAll(
      '.product-card-wrapper--surface-collection .card > .card__content, ' +
      '.product-card-wrapper--surface-search .card > .card__content'
    )].filter((body) => body.offsetParent !== null);

  const measureGrid = (grid) => {
    const bodies = getSyncBodies(grid);
    if (!bodies.length) {
      grid.classList.remove('cbh-card-heights-synced');
      grid.style.removeProperty('--cbh-synced-card-content-height');
      return;
    }

    /* Remove the previous synchronised height so scrollHeight reflects each
       card's genuine current content after filters, ratings and responsive
       wrapping have settled. */
    grid.classList.remove('cbh-card-heights-synced');
    grid.style.removeProperty('--cbh-synced-card-content-height');

    let tallest = 0;
    for (const body of bodies) {
      tallest = Math.max(tallest, Math.ceil(body.scrollHeight));
    }

    if (tallest > 0) {
      grid.style.setProperty('--cbh-synced-card-content-height', `${tallest}px`);
      grid.classList.add('cbh-card-heights-synced');
    }
  };

  const syncAllCardHeights = (root = document) => {
    getProductGrids(root).forEach(measureGrid);
  };

  const scheduleSync = (root = document) => {
    window.cancelAnimationFrame(syncFrame);
    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = 0;
      syncAllCardHeights(root);
    });
  };

  const observeGrid = (grid) => {
    if (!grid || observedGrids.has(grid)) return;
    observedGrids.add(grid);

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.type === 'childList')) return;
      scheduleSync(grid);
    });
    observer.observe(grid, { childList: true, subtree: true });
    mutationObservers.set(grid, observer);

    if (gridResizeObserver && !resizeObservedGrids.has(grid)) {
      resizeObservedGrids.add(grid);
      gridResizeObserver.observe(grid);
    }
  };

  const initialiseHeightSync = (root = document) => {
    getProductGrids(root).forEach((grid) => {
      if (getSyncBodies(grid).length) observeGrid(grid);
    });
    scheduleSync(root);
  };

  const cleanupGrid = (grid) => {
    if (!grid) return;

    mutationObservers.get(grid)?.disconnect();
    mutationObservers.delete(grid);

    if (gridResizeObserver && resizeObservedGrids.has(grid)) {
      gridResizeObserver.unobserve(grid);
      resizeObservedGrids.delete(grid);
    }

    observedGrids.delete(grid);
    grid.classList.remove('cbh-card-heights-synced');
    grid.style.removeProperty('--cbh-synced-card-content-height');
  };

  const cleanupScope = (root) => {
    getProductGrids(root || document).forEach(cleanupGrid);
  };

  const start = () => {
    initialiseHeightSync(document);

    if (document.querySelector('product-recommendations .product-card-wrapper')) {
      scheduleRatingHydration();
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleSync(document));
    }

    if (!gridResizeObserver) {
      window.addEventListener('resize', () => scheduleSync(document), { passive: true });
    }
    window.addEventListener('load', () => scheduleSync(document), { once: true });

    document.addEventListener('cbh:collection:updated', () => {
      scheduleRatingHydration();
      initialiseHeightSync(document);
    });

    document.addEventListener('cbh:product-recommendations:updated', (event) => {
      scheduleRatingHydration();
      scheduleSync(event.detail?.container || document);
    });

    document.addEventListener('shopify:section:load', (event) => {
      const scope = event.target || document;
      initialiseHeightSync(scope);
      if (scope.querySelector?.('.product-card-wrapper')) scheduleRatingHydration();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      cleanupScope(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
